import { Tooltip } from 'antd';
import React from 'react';
import { useDispatch } from 'react-redux';

import Icon from '@ant-design/icons';
import {
  EPanelOpenMethod,
  useBlockEventSinglePanelStore,
} from '@components/block-event-single/store';
import ButtonKit from '@components/common/ButtonKit';
import { useDataTransformPanelStore } from '@components/data-transform-panel/store';
import { useDimensionPanelStore } from '@components/dimensions/store';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import { QUICK_MAP_MODAL_KEY } from '@components/quick-map';
import BlockIcon from '@icons/block-event/block.svg';
import PlusIcon from '@icons/emp/plus_icon.svg';
import QuickMapIcon from '@icons/emp/quick_map_icon.svg';
import NewCustomDimensionIcon from '@icons/metric-template/metric-builder.svg';
import DataTransformIcon from '@icons/rename-page/data-transform.svg';
import { ERenamePreset } from '@src/common/constant/enum';
import { useCustomDimensionPanelStore } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/store';
import DeployButton from '@src/pages/main/components/DeployButton';
import { WorkbenchDeployButtons } from '@src/pages/main/components/DeployButton/workbench';
import { StyledWrapper } from '@src/pages/main/components/MappingEntrance/styled';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import { trackClickMapNewEvent, trackNewCustomDimension } from '@src/sensor';
import { setMappingModePreset } from '@store/common/actions';
import { useMainStore } from '../../store';
import { useCustomDimensionStore } from 'emp-3.0/components/Dimensions/store';
import { useDimensionWorkflow } from '@hooks';
import { useNavigate } from 'react-router-dom';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
const VerticalSpacer = () => (
  <div style={{ width: '1px', height: '28px', background: 'rgba(0, 0, 0, 0.15)' }} />
);

const MappingEntrance: React.FC = () => {
  const { workbenchMode } = useInitialInfoStore();
  const navigate = useNavigate();
  const isNewWorkflow = useDimensionWorkflow();
  const openModal = useMainStore((state) => state.openModal);
  const [setMappingPanelOpen] = useMappingPanelStore((state) => [state.setMappingPanelOpen]);
  const [setTransformPanelOpen] = useDataTransformPanelStore((state) => [
    state.setTransformPanelOpen,
  ]);
  const setCurPresetName = useDimensionPanelStore((state) => state.setCurPresetName);
  const [setBlockEventSingleOpen, setPanelOpenMethod] = useBlockEventSinglePanelStore((state) => [
    state.setBlockEventSingleOpen,
    state.setPanelOpenMethod,
  ]);
  const { setCustomDimensionPanelOpen } = useCustomDimensionPanelStore((state) => state);
  const { setCreateFrom } = useCustomDimensionStore((state) => state);
  const dispatch = useDispatch();

  const handleMapNew = () => {
    // report to conviva sensor
    trackClickMapNewEvent({
      elementType: 'div',
      elementId: 'map_new_event_entry_btn',
    });
    setMappingPanelOpen(true);
    dispatch(setMappingModePreset()); // will remove preset later phase.
    setCurPresetName('Explore');
  };

  const handleQuickMap = () => {
    openModal(QUICK_MAP_MODAL_KEY);
  };

  const handleClickRenamePageButton = () => {
    if (isNewWorkflow) {
      navigate(`/workflow/rename/new`);
      return;
    }
    setTransformPanelOpen(true);
    setCurPresetName(ERenamePreset.PAGE);
  };

  const handleBlockEvent = () => {
    setBlockEventSingleOpen(true);
    setPanelOpenMethod(EPanelOpenMethod.CREATE);
  };

  const handleCustomDimension = () => {
    trackNewCustomDimension({
      elementType: 'div',
      elementId: 'new_custom_dimension_entry_btn',
      type: 'create',
      isNewWorkflow,
    });
    if (isNewWorkflow) {
      setCreateFrom('otherEntry');
      navigate(`/workflow/dimension/new`);
      return;
    }
    setCustomDimensionPanelOpen(true);
  };

  return (
    <StyledWrapper>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <PermissionButton
          tooltip="Create semantic events from raw events."
          onClick={handleMapNew}
          styleType="styled"
          type="primary"
        >
          <>
            <Icon component={PlusIcon} style={{ fontSize: 16, marginRight: 4 }} /> Map New Event
          </>
        </PermissionButton>
        <PermissionButton
          tooltip="Convert raw events to mapped events in bulk without additional mapping rules."
          onClick={handleQuickMap}
          styleType="styled"
        >
          <>
            <Icon
              component={QuickMapIcon}
              style={{ fontSize: 16, marginRight: 4, fill: '#0046ff' }}
            />
            Quick Map
          </>
        </PermissionButton>

        <VerticalSpacer />
        {!workbenchMode && (
          <>
            <PermissionButton
              tooltip="Block the reporting of invalid or meaningless events from the sensor."
              onClick={handleBlockEvent}
              styleType="styled"
            >
              <Tooltip
                title=""
                overlayStyle={{ maxWidth: 'fit-content' }}
                overlayInnerStyle={{ maxWidth: 'fit-content' }}
                arrow
                placement="topLeft"
              >
                <>
                  <Icon
                    component={BlockIcon}
                    style={{ fontSize: 16, marginRight: 4, fill: '#0046ff' }}
                  />
                  Block Event
                </>
              </Tooltip>
            </PermissionButton>
            <VerticalSpacer />
          </>
        )}

        <PermissionButton tooltip="" onClick={handleCustomDimension} styleType="styled">
          <>
            <Icon
              component={NewCustomDimensionIcon}
              style={{ fontSize: 16, marginRight: 4, fill: '#0046ff' }}
            />
            New Custom Dimension
          </>
        </PermissionButton>

        <VerticalSpacer />
        <div
          style={{ paddingLeft: '4px', color: 'rgba(0,0,0,0.65)', fontWeight: 500, fontSize: 14 }}
        >
          <Icon style={{ fontSize: '16px' }} component={DataTransformIcon} /> Data Transform :
        </div>
        <PermissionButton
          tooltip="Semanticize web page/mobile screen titles by renaming or identifying them with URLs."
          onClick={handleClickRenamePageButton}
          styleType="styled"
        >
          <>Rename Page/Screen</>
          {/* <ButtonKit
            kitType="primary"
            borderRadius="2px"
            data-test-id="rename_page_entry_btn"
          >
            Rename Page/Screen
          </ButtonKit> */}
        </PermissionButton>
        {/* <Tooltip
          title="Semanticize web page/mobile screen titles by renaming or identifying them with URLs."
          overlayStyle={{ maxWidth: 'fit-content' }}
          overlayInnerStyle={{ maxWidth: 'fit-content' }}
          arrow
          placement="topLeft"
        >
          
        </Tooltip> */}
      </div>
      {workbenchMode ? <WorkbenchDeployButtons /> : <DeployButton />}
    </StyledWrapper>
  );
};

export default MappingEntrance;
